import { logger } from "../lib/logger";
import { ErrorFactory } from "../lib/error-handling";
import { supabase } from "../lib/supabase";
import { SupportTicketNotificationService } from "./SupportTicketNotificationService";

export interface CustomerInfo {
  planType: string | null; // 'free' | 'solo' | 'starter_*' | 'pro' | 'ltd_*' | null
  subscriptionStatus: string | null; // 'active' | 'past_due' | 'cancelled' | 'trialing' | 'paused' | null
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface SupportTicket {
  id: string;
  email: string;
  subject: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority?: number; // 0=low, 1=normal, 2=high, 3=critical
  severity?: string; // "low" | "normal" | "high" | "critical" | "blocker"
  type?: string; // "bug" | "feature" | "question" | "feedback" | "other"
  metadata?: any;
  created_at: string;
  updated_at: string;
  // Champs d'assignation
  assigned_to?: string; // UUID du profil assigné
  assigned_at?: string; // Timestamp de l'assignation
  assigned_by?: string; // UUID de qui a assigné
  assigned_email?: string; // Email de la personne assignée (pour l'affichage)
  is_assigned?: boolean; // Helper pour l'affichage
  // Info client (jointure profiles par email)
  customer?: CustomerInfo | null;
}

export interface SupportMessage {
  id: string;
  ticket_id: string;
  sender_type: "user" | "agent" | "ia" | "system";
  sender_email: string;
  content: string;
  content_type: "text" | "html";
  metadata?: any;
  content_attributes?: {
    draft_author?: string;
    draft_kind?: "fix" | "info-request";
    pr_url?: string;
    [key: string]: any;
  } | null;
  status: "draft" | "sent";
  created_at: string;
}

export interface CreateTicketParams {
  email: string;
  subject: string;
  content: string;
  priority?: number;
  severity?: string;
  type?: string;
  metadata?: any;
}

export class SupportTicketService {
  private static instance: SupportTicketService;
  private supabase;

  private constructor() {
    this.supabase = supabase;
  }

  public static getInstance(): SupportTicketService {
    if (!SupportTicketService.instance) {
      SupportTicketService.instance = new SupportTicketService();
    }
    return SupportTicketService.instance;
  }

  /**
   * Liste tous les tickets (réservé aux admins)
   */
  async listTickets(
    options: {
      status?: string[];
      type?: string[];
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<SupportTicket[]> {
    try {
      // Use the supabase-js client so the user's session JWT is sent with
      // the request. The view runs with security_invoker=on, which means
      // RLS on support_tickets is evaluated as the caller — admin reads
      // only succeed when is_admin() / the JWT-based admin policies apply.
      // Safety net : la requête supabase-js peut hang indéfiniment (même
      // pattern que le fix sur getFeatureRequests). On race contre un
      // timeout 10s pour ne jamais laisser /admin en spinner infini.
      const queryPromise = this.supabase
        .from("support_tickets_with_assignment")
        .select("*")
        .order("created_at", { ascending: false });
      const { data, error } = (await Promise.race([
        queryPromise,
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("listTickets timeout after 10s")),
            10000,
          ),
        ),
      ])) as Awaited<typeof queryPromise>;

      if (error) {
        throw new Error(`${error.code ?? "SUPABASE_ERROR"}: ${error.message}`);
      }

      // Apply filters client-side since the view returns all tickets
      let filteredData = data || [];

      if (options.status && options.status.length > 0) {
        filteredData = filteredData.filter((t: any) =>
          options.status!.includes(t.status),
        );
      }

      if (options.type && options.type.length > 0) {
        filteredData = filteredData.filter(
          (t: any) => t.type && options.type!.includes(t.type),
        );
      }

      if (options.limit) {
        filteredData = filteredData.slice(0, options.limit);
      }

      if (options.offset) {
        const start = options.offset;
        const end = start + (options.limit || 10);
        filteredData = filteredData.slice(start, end);
      }

      // Enrichir avec les infos client (plan, subscription) par email
      await this.enrichTicketsWithCustomerInfo(filteredData);

      return filteredData;
    } catch (error) {
      console.error("🎫 [SupportTicketService] Failed to list tickets:", error);
      logger.error("Failed to list tickets", "support", error);
      throw ErrorFactory.network("Erreur lors de la récupération des tickets");
    }
  }

  /**
   * Récupère un ticket par son ID avec ses messages
   */
  async getTicketDetails(
    ticketId: string,
  ): Promise<{ ticket: SupportTicket; messages: SupportMessage[] }> {
    try {
      // Direct fetch to bypass Supabase client issues
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      // Fetch ticket
      const ticketResponse = await fetch(
        `${supabaseUrl}/rest/v1/support_tickets?id=eq.${ticketId}&select=*`,
        {
          headers: {
            apikey: supabaseKey,
            Authorization: `Bearer ${supabaseKey}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!ticketResponse.ok) {
        const errorText = await ticketResponse.text();
        throw new Error(`HTTP ${ticketResponse.status}: ${errorText}`);
      }

      const ticketData = await ticketResponse.json();
      const ticket = ticketData[0];

      if (!ticket) {
        throw new Error("Ticket not found");
      }

      // Enrichir avec les infos client
      await this.enrichTicketsWithCustomerInfo([ticket]);

      // Fetch messages
      const messagesResponse = await fetch(
        `${supabaseUrl}/rest/v1/support_messages?ticket_id=eq.${ticketId}&select=*&order=created_at.asc`,
        {
          headers: {
            apikey: supabaseKey,
            Authorization: `Bearer ${supabaseKey}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!messagesResponse.ok) {
        const errorText = await messagesResponse.text();
        throw new Error(`HTTP ${messagesResponse.status}: ${errorText}`);
      }

      const messages = await messagesResponse.json();

      return { ticket, messages: messages || [] };
    } catch (error) {
      console.error(
        "🎫 [SupportTicketService] Failed to get ticket details:",
        error,
      );
      logger.error(
        `Failed to get ticket details for ${ticketId}`,
        "support",
        error,
      );
      throw ErrorFactory.network(
        "Erreur lors de la récupération des détails du ticket",
      );
    }
  }

  /**
   * Met à jour le statut, la priorité ou l'assignation d'un ticket
   */
  async updateTicket(
    ticketId: string,
    updates: Partial<
      Pick<
        SupportTicket,
        | "status"
        | "priority"
        | "type"
        | "metadata"
        | "assigned_to"
        | "assigned_at"
        | "assigned_by"
      >
    >,
  ): Promise<SupportTicket> {
    try {
      // D'abord vérifier si le ticket existe et récupérer l'ancien statut
      const { data: existingTicket, error: checkError } = await this.supabase
        .from("support_tickets")
        .select("*")
        .eq("id", ticketId)
        .single();

      if (checkError || !existingTicket) {
        throw ErrorFactory.notFound(`Ticket ${ticketId} not found`);
      }

      const oldStatus = existingTicket.status;

      // Utiliser la fonction RPC pour contourner les problèmes RLS
      console.log("Using RPC function to update ticket (bypass RLS)...");
      const { data, error } = await this.supabase.rpc("update_ticket_status", {
        ticketid: ticketId,
        status: updates.status,
        assigned_to: updates.assigned_to,
        assigned_at: updates.assigned_at,
        assigned_by: updates.assigned_by,
      });

      if (error) {
        console.error("RPC update failed:", error);
        throw error;
      }

      // La fonction RPC retourne { success: true, ticket: SupportTicket }
      if (!data || !data.success) {
        throw new Error(data?.error || "RPC update failed");
      }

      const updatedTicket = data.ticket;

      // Envoyer la notification de statut si nécessaire (en arrière-plan)
      if (updates.status && updates.status !== oldStatus) {
        console.log("=== STATUS CHANGE DETECTED ===");
        console.log("Old status:", oldStatus);
        console.log("New status:", updates.status);

        // Envoyer la notification en arrière-plan sans bloquer
        this.sendStatusNotificationAsync(
          updatedTicket,
          oldStatus,
          updates.status,
        );
      }

      return updatedTicket;
    } catch (error) {
      logger.error(`Failed to update ticket ${ticketId}`, "support", {
        error,
        ticketId,
        updates,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      throw ErrorFactory.network("Erreur lors de la mise à jour du ticket");
    }
  }

  /**
   * Envoie la notification de statut en arrière-plan (non bloquant)
   */
  private async sendStatusNotificationAsync(
    ticket: SupportTicket,
    oldStatus: string,
    newStatus: string,
  ): Promise<void> {
    try {
      console.log("Sending status notification for ticket:", ticket.id);

      const notificationService =
        SupportTicketNotificationService.getInstance();
      const result = await notificationService.notifyStatusChange(
        ticket,
        oldStatus,
        newStatus,
        ticket.assigned_email || undefined,
      );

      if (result.success) {
        console.log("Status notification sent successfully:", result.emailId);
      } else {
        console.warn("Status notification failed:", result.error);
      }
    } catch (error) {
      // Ne pas bloquer la mise à jour du ticket si la notification échoue
      console.error(
        "Failed to send status notification (non-blocking):",
        error,
      );
      logger.warn(
        "Status notification failed but ticket was updated successfully",
        "support",
        { ticketId: ticket.id, error },
      );
    }
  }

  /**
   * Supprime définitivement un ticket de la base de données
   */
  async deleteTicket(ticketId: string): Promise<void> {
    try {
      // Vérifier que le ticket existe
      const { data: existingTicket, error: checkError } = await this.supabase
        .from("support_tickets")
        .select("id, subject")
        .eq("id", ticketId)
        .single();

      if (checkError || !existingTicket) {
        throw new Error(`Ticket ${ticketId} not found`);
      }

      // Essayer la fonction RPC admin d'abord (bypass RLS)
      const { data: rpcResult, error: rpcError } = await this.supabase.rpc(
        "admin_delete_support_ticket",
        { ticket_id: ticketId },
      );

      if (rpcError) {
        logger.warn(
          `RPC admin_delete failed, falling back to direct delete: ${rpcError.message}`,
          "support",
        );

        // Fallback vers le client Supabase direct
        const { error } = await this.supabase
          .from("support_tickets")
          .delete()
          .eq("id", ticketId);

        if (error) throw error;
      }

      logger.info(
        `Ticket ${ticketId} deleted successfully via ${rpcError ? "direct" : "RPC admin"}`,
        "support",
      );
    } catch (error) {
      logger.error(`Failed to delete ticket ${ticketId}`, "support", {
        error,
        ticketId,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      throw ErrorFactory.network("Erreur lors de la suppression du ticket");
    }
  }

  /**
   * Ajoute un message à un ticket
   */
  async addMessage(
    ticketId: string,
    content: string,
    senderType: "user" | "agent" | "system" = "agent",
    senderEmail?: string,
  ): Promise<SupportMessage> {
    try {
      console.log(
        "🎫 [SupportTicketService] addMessage called for ticket:",
        ticketId,
      );

      // Direct fetch to bypass Supabase client issues
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const messageData = {
        ticket_id: ticketId,
        sender_type: senderType,
        sender_email: senderEmail || "admin@doodates.com",
        content,
        content_type: "text",
        created_at: new Date().toISOString(),
      };

      const response = await fetch(`${supabaseUrl}/rest/v1/support_messages`, {
        method: "POST",
        headers: {
          apikey: supabaseKey,
          Authorization: `Bearer ${supabaseKey}`,
          "Content-Type": "application/json",
          Prefer: "return=representation",
        },
        body: JSON.stringify(messageData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "🎫 [SupportTicketService] Add message error:",
          response.status,
          errorText,
        );
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      const message = Array.isArray(data) ? data[0] : data;

      console.log("🎫 [SupportTicketService] Message added successfully");

      return message;
    } catch (error) {
      console.error("🎫 [SupportTicketService] Failed to add message:", error);
      logger.error(
        `Failed to add message to ticket ${ticketId}`,
        "support",
        error,
      );
      throw ErrorFactory.network("Erreur lors de l'envoi du message");
    }
  }

  /**
   * Marque un message draft comme envoyé. Utilisé par l'UI admin quand
   * un brouillon IA (Claude triage) est validé et envoyé à l'utilisateur.
   * Utilise le client supabase authentifié — la policy RLS `admin_all_messages`
   * (is_admin()) autorise l'UPDATE.
   */
  async sendDraft(messageId: string): Promise<SupportMessage> {
    const { data, error } = await this.supabase
      .from("support_messages")
      .update({ status: "sent" })
      .eq("id", messageId)
      .select("*")
      .single();

    if (error) {
      throw new Error(error.message);
    }
    return data as SupportMessage;
  }

  /**
   * Édite le contenu d'un draft avant envoi.
   */
  async editDraft(messageId: string, content: string): Promise<SupportMessage> {
    const { data, error } = await this.supabase
      .from("support_messages")
      .update({ content })
      .eq("id", messageId)
      .eq("status", "draft")
      .select("*")
      .single();

    if (error) {
      throw new Error(error.message);
    }
    return data as SupportMessage;
  }

  /**
   * Rejette (supprime) un draft.
   */
  async rejectDraft(messageId: string): Promise<void> {
    const { error } = await this.supabase
      .from("support_messages")
      .delete()
      .eq("id", messageId)
      .eq("status", "draft");

    if (error) {
      throw new Error(error.message);
    }
  }

  /**
   * Crée un nouveau ticket support (utilisé par SentryTicketBridge et RecentProblemsDetector)
   */
  async createTicket(params: CreateTicketParams): Promise<SupportTicket> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const body = {
        email: params.email,
        subject: params.subject,
        type: params.type ?? "bug",
        severity: params.severity ?? "normal",
        priority: params.priority ?? 1,
        status: "open",
        metadata: params.metadata ?? {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const response = await fetch(`${supabaseUrl}/rest/v1/support_tickets`, {
        method: "POST",
        headers: {
          apikey: supabaseKey,
          Authorization: `Bearer ${supabaseKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const text = await response.text();
      if (!text.trim()) {
        throw new Error("Empty response from server");
      }

      const ticket = JSON.parse(text);
      return Array.isArray(ticket) ? ticket[0] : ticket;
    } catch (error) {
      console.error(
        "🎫 [SupportTicketService] Failed to create ticket:",
        error,
      );
      logger.error("Failed to create ticket", "support", error);
      throw ErrorFactory.network("Erreur lors de la création du ticket");
    }
  }

  /**
   * Récupère l'historique d'un utilisateur par email, incluant les infos client
   * (plan, statut d'abonnement LemonSqueezy).
   */
  async getUserHistory(email: string): Promise<{
    ticketsCount: number;
    customer: CustomerInfo | null;
  }> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      // 1. Compter les tickets
      const ticketsResponse = await fetch(
        `${supabaseUrl}/rest/v1/support_tickets?email=eq.${encodeURIComponent(email)}&select=id`,
        {
          headers: {
            apikey: supabaseKey,
            Authorization: `Bearer ${supabaseKey}`,
            "Content-Type": "application/json",
          },
        },
      );

      const tickets = ticketsResponse.ok ? await ticketsResponse.json() : [];

      // 2. Récupérer le profil (plan, subscription) par email
      const customer = await this.fetchCustomerInfoByEmail(
        email,
        supabaseUrl,
        supabaseKey,
      );

      return {
        ticketsCount: tickets.length || 0,
        customer,
      };
    } catch (error) {
      logger.warn(`Failed to get user history for ${email}`, "support", error);
      return { ticketsCount: 0, customer: null };
    }
  }

  /**
   * Récupère les infos client (plan + subscription) d'un email unique.
   * Retourne null si aucun profil n'est trouvé.
   */
  private async fetchCustomerInfoByEmail(
    email: string,
    _supabaseUrl: string,
    _supabaseKey: string,
  ): Promise<CustomerInfo | null> {
    try {
      // Utilise le client Supabase authentifié pour bénéficier de la session
      // admin (et contourner les policies RLS qui bloqueraient une requête
      // anon sur `profiles`).
      const { data, error } = await this.supabase
        .from("profiles")
        .select(
          "plan_type, lemonsqueezy_status, lemonsqueezy_current_period_end, lemonsqueezy_cancel_at_period_end",
        )
        .eq("email", email)
        .limit(1);
      if (error) {
        logger.warn(
          `Failed to fetch customer info for ${email}`,
          "support",
          error,
        );
        return null;
      }
      const profile = data?.[0];
      if (!profile) return null;
      return {
        planType: profile.plan_type ?? null,
        subscriptionStatus: profile.lemonsqueezy_status ?? null,
        periodEnd: profile.lemonsqueezy_current_period_end ?? null,
        cancelAtPeriodEnd: !!profile.lemonsqueezy_cancel_at_period_end,
      };
    } catch {
      return null;
    }
  }

  /**
   * Enrichit en place une liste de tickets avec leur info client (jointure
   * batch sur la table profiles via email=in.(...)). Tolérant aux erreurs :
   * en cas d'échec, les tickets restent sans info client.
   */
  private async enrichTicketsWithCustomerInfo(
    tickets: SupportTicket[],
  ): Promise<void> {
    if (!tickets || tickets.length === 0) return;
    try {
      const uniqueEmails = Array.from(
        new Set(tickets.map((t) => t.email).filter(Boolean)),
      );
      if (uniqueEmails.length === 0) return;

      // Client Supabase authentifié → propage la session admin pour lever
      // les policies RLS sur `profiles` (l'anon key ne peut rien lire).
      const { data: profiles, error } = await this.supabase
        .from("profiles")
        .select(
          "email, plan_type, lemonsqueezy_status, lemonsqueezy_current_period_end, lemonsqueezy_cancel_at_period_end",
        )
        .in("email", uniqueEmails);

      if (error || !profiles) {
        logger.warn(
          "Failed to enrich tickets with customer info",
          "support",
          error,
        );
        return;
      }

      const byEmail = new Map<string, CustomerInfo>();
      for (const p of profiles) {
        byEmail.set(p.email, {
          planType: p.plan_type ?? null,
          subscriptionStatus: p.lemonsqueezy_status ?? null,
          periodEnd: p.lemonsqueezy_current_period_end ?? null,
          cancelAtPeriodEnd: !!p.lemonsqueezy_cancel_at_period_end,
        });
      }

      for (const t of tickets) {
        t.customer = byEmail.get(t.email) ?? null;
      }
    } catch (error) {
      logger.warn(
        "Failed to enrich tickets with customer info",
        "support",
        error,
      );
    }
  }
}

export const supportTicketService = SupportTicketService.getInstance();
